import { z } from 'zod';

import { CONTACT_TYPES } from './contacts.ts';
import { DEAL_OPERATIONS } from './deals.ts';
import { OPERATIONS, PROPERTY_TYPES, type PropertyType } from './properties.ts';

/*
 * Imports from Excel/CSV (HU-CRM-06 contacts and leads, HU-INV-19 properties).
 * The field catalog drives the downloadable template, the automatic column mapping
 * (by the column titles) and the preview. Rules shared by the API and the web.
 */

export const IMPORT_KINDS = { contacts: 'Contactos', properties: 'Propiedades' } as const;
export type ImportKind = keyof typeof IMPORT_KINDS;

export const IMPORT_LIMITS = {
  maxBytes: 5 * 1024 * 1024,
  maxRows: 5000,
  maxColumns: 100,
  /** Rows created per request (the screen shows the progress). */
  batchSize: 250,
  /** Days during which an import can be undone. */
  undoDays: 7,
} as const;

/** The legal basis the importer chooses for the whole file (never marketing consent). */
export const IMPORT_LEGAL_BASES = {
  prior_relationship: 'Relación comercial previa',
  legitimate_interest: 'Interés legítimo',
} as const;
export type ImportLegalBasis = keyof typeof IMPORT_LEGAL_BASES;

export interface ImportField {
  /** Column title in the template (Spanish). */
  label: string;
  /** Other titles recognized by the automatic mapping (compared without accents). */
  synonyms: readonly string[];
  required?: boolean;
  /** Shown in the template's instructions. */
  help: string;
}

export const CONTACT_IMPORT_FIELDS = {
  name: {
    label: 'Nombre',
    synonyms: ['nombre completo', 'cliente', 'contacto', 'nombre y apellido', 'nombres'],
    required: true,
    help: 'Nombre completo de la persona o razón social de la empresa.',
  },
  kind: {
    label: 'Tipo',
    synonyms: ['tipo de contacto', 'persona o empresa'],
    help: 'Persona o Empresa. Si está vacío: Persona.',
  },
  phone: {
    label: 'Teléfono',
    synonyms: ['telefono 1', 'celular', 'movil', 'tel', 'whatsapp', 'numero'],
    help: '8 dígitos (7123 4567) o con código de país (+1 305...). Teléfono o correo: al menos uno.',
  },
  phone2: {
    label: 'Teléfono 2',
    synonyms: ['otro telefono', 'telefono fijo', 'telefono casa', 'telefono trabajo'],
    help: 'Opcional.',
  },
  email: {
    label: 'Correo',
    synonyms: ['email', 'e-mail', 'correo electronico', 'mail'],
    help: 'Teléfono o correo: al menos uno.',
  },
  nationalId: {
    label: 'DUI',
    synonyms: ['documento', 'documento de identidad', 'numero de dui'],
    help: 'Opcional. 8 dígitos, guion y dígito verificador. Se guarda cifrado.',
  },
  address: {
    label: 'Dirección',
    synonyms: ['direccion exacta', 'domicilio'],
    help: 'Opcional. Se guarda cifrada.',
  },
  district: {
    label: 'Distrito',
    synonyms: ['municipio', 'ciudad', 'localidad', 'zona'],
    help: 'Santa Tecla, Antiguo Cuscatlán, San Salvador... (los nombres de los antiguos municipios).',
  },
  department: {
    label: 'Departamento',
    synonyms: ['depto'],
    help: 'Solo si el nombre del distrito se repite en otro departamento.',
  },
  channel: {
    label: 'Canal de origen',
    synonyms: ['canal', 'origen', 'fuente', 'medio'],
    help: 'Uno de los canales configurados (Facebook, Referido...).',
  },
  types: {
    label: 'Papel',
    synonyms: ['papeles', 'tipo de cliente', 'rol', 'categoria'],
    help: `Uno o varios separados por coma: ${Object.values(CONTACT_TYPES).join(', ')}.`,
  },
  tags: {
    label: 'Etiquetas',
    synonyms: ['etiqueta', 'tags'],
    help: 'Separadas por coma; solo etiquetas que ya existen.',
  },
  assignedTo: {
    label: 'Agente',
    synonyms: ['asesor', 'asignado', 'asignado a', 'responsable', 'vendedor', 'correo del agente'],
    help: 'Correo o nombre del agente. Si está vacío: el agente elegido para todo el archivo.',
  },
  notes: {
    label: 'Notas',
    synonyms: ['observaciones', 'comentarios', 'nota'],
    help: 'Notas privadas.',
  },
  dealOperation: {
    label: 'Operación del negocio',
    synonyms: ['operacion', 'busca', 'interes'],
    help: `Solo si eliges crear negocios: ${Object.values(DEAL_OPERATIONS).join(' o ')}.`,
  },
} as const satisfies Record<string, ImportField>;
export type ContactImportField = keyof typeof CONTACT_IMPORT_FIELDS;

export const PROPERTY_IMPORT_FIELDS = {
  title: {
    label: 'Título',
    synonyms: ['nombre', 'titulo del anuncio', 'propiedad'],
    required: true,
    help: 'Casa con jardín en Residencial Las Palmas.',
  },
  type: {
    label: 'Tipo',
    synonyms: ['tipo de propiedad', 'tipo de inmueble'],
    required: true,
    help: `${Object.values(PROPERTY_TYPES)
      .map((t) => t.label)
      .join(', ')}.`,
  },
  operation: {
    label: 'Operación',
    synonyms: ['venta o alquiler', 'operacion'],
    required: true,
    help: `${Object.values(OPERATIONS).join(', ')}.`,
  },
  salePrice: {
    label: 'Precio de venta',
    synonyms: ['precio', 'precio venta', 'valor'],
    help: 'En USD, sin signo o con él ($185,000).',
  },
  rentPrice: {
    label: 'Precio de alquiler',
    synonyms: ['alquiler', 'renta', 'canon', 'precio alquiler'],
    help: 'En USD al mes.',
  },
  district: {
    label: 'Distrito',
    synonyms: ['municipio', 'ciudad', 'localidad'],
    required: true,
    help: 'Santa Tecla, Antiguo Cuscatlán, San Salvador...',
  },
  department: {
    label: 'Departamento',
    synonyms: ['depto'],
    help: 'Solo si el nombre del distrito se repite en otro departamento.',
  },
  neighborhood: {
    label: 'Zona o colonia',
    synonyms: ['colonia', 'residencial', 'zona', 'barrio', 'urbanizacion'],
    help: 'Se muestra en la ficha pública.',
  },
  address: {
    label: 'Dirección exacta',
    synonyms: ['direccion'],
    help: 'Privada; se guarda cifrada.',
  },
  cadastralCode: {
    label: 'Código catastral',
    synonyms: ['catastro', 'matricula', 'numero de matricula'],
    help: 'Opcional; sirve para detectar duplicados.',
  },
  bedrooms: { label: 'Habitaciones', synonyms: ['cuartos', 'dormitorios', 'recamaras'], help: '' },
  bathrooms: { label: 'Baños', synonyms: ['banos'], help: 'Medios baños con .5 (2.5).' },
  parkingSpaces: { label: 'Parqueos', synonyms: ['estacionamientos', 'cocheras'], help: '' },
  builtArea: {
    label: 'Área construida',
    synonyms: ['construccion', 'area de construccion'],
    help: '',
  },
  lotArea: {
    label: 'Área de terreno',
    synonyms: ['terreno', 'area del terreno', 'lote'],
    help: '',
  },
  lotAreaUnit: {
    label: 'Unidad del terreno',
    synonyms: ['unidad de area', 'unidad', 'unidad de terreno', 'unid terreno'],
    help: 'm² o v². Si está vacío: m².',
  },
  builtAreaUnit: {
    label: 'Unidad de construcción',
    synonyms: ['unidad construccion', 'unid construccion'],
    help: 'm² o v². Si está vacío: m².',
  },
  levels: { label: 'Niveles', synonyms: ['plantas', 'pisos'], help: '' },
  yearBuilt: { label: 'Año de construcción', synonyms: ['ano de construccion', 'ano'], help: '' },
  maintenanceFee: {
    label: 'Cuota de mantenimiento',
    synonyms: ['mantenimiento'],
    help: 'USD al mes.',
  },
  description: { label: 'Descripción', synonyms: ['descripcion del anuncio', 'detalle'], help: '' },
  titleEn: {
    label: 'Título en inglés',
    synonyms: ['titulo ingles', 'title', 'english title'],
    help: 'Opcional. El sitio en inglés solo muestra las propiedades con título en inglés.',
  },
  descriptionEn: {
    label: 'Descripción en inglés',
    synonyms: ['descripcion ingles', 'description', 'english description'],
    help: 'Opcional; necesita el título en inglés.',
  },
  exclusive: {
    label: 'Exclusiva',
    synonyms: ['exclusividad'],
    help: 'Sí o No. Una exclusiva necesita la fecha de fin del acuerdo.',
  },
  listingStartsOn: {
    label: 'Inicio del acuerdo',
    synonyms: ['fecha de captacion', 'inicio de captacion'],
    help: 'dd/mm/aaaa.',
  },
  listingEndsOn: {
    label: 'Fin del acuerdo',
    synonyms: ['vencimiento', 'fin de captacion', 'vence'],
    help: 'dd/mm/aaaa.',
  },
  commissionSalePct: {
    label: 'Comisión de venta (%)',
    synonyms: ['comision', 'comision venta', '% comision'],
    help: '5 = 5 %.',
  },
  commissionRentMonths: {
    label: 'Comisión de alquiler (meses)',
    synonyms: ['comision alquiler'],
    help: '1 = un mes de alquiler.',
  },
  assignedTo: {
    label: 'Agente',
    synonyms: ['asesor', 'captador', 'asignado', 'responsable'],
    help: 'Correo o nombre del agente. Si está vacío: el agente elegido para todo el archivo.',
  },
  ownerName: {
    label: 'Propietario',
    synonyms: ['nombre del propietario', 'dueno', 'nombre del dueno'],
    required: true,
    help: 'Si ya existe (mismo teléfono, correo o DUI), se usa el contacto existente.',
  },
  ownerPhone: {
    label: 'Teléfono del propietario',
    synonyms: ['telefono propietario', 'telefono del dueno', 'celular propietario'],
    help: 'Teléfono o correo del propietario: al menos uno.',
  },
  ownerEmail: {
    label: 'Correo del propietario',
    synonyms: ['correo propietario', 'email propietario', 'email del dueno'],
    help: '',
  },
  ownerNationalId: {
    label: 'DUI del propietario',
    synonyms: ['dui propietario', 'documento del propietario'],
    help: 'Opcional; se guarda cifrado.',
  },
  privateNotes: {
    label: 'Notas privadas',
    synonyms: ['notas', 'observaciones', 'comentarios'],
    help: '',
  },
} as const satisfies Record<string, ImportField>;
export type PropertyImportField = keyof typeof PROPERTY_IMPORT_FIELDS;

/**
 * The property list RECONNECT (RE/MAX's platform) exports: recognized by its column
 * titles and mapped automatically (the user does not map it). Labels are the titles
 * as RECONNECT writes them. No owners come in it.
 */
export const RECONNECT_PROPERTY_FIELDS = {
  externalId: { label: 'Listing ID', synonyms: [], required: true, help: 'Código de RE/MAX.' },
  title: { label: 'Título', synonyms: [], required: true, help: '' },
  operation: {
    label: 'Tipo de operación',
    synonyms: [],
    required: true,
    help: 'Venta o Alquiler.',
  },
  type: { label: 'Tipo', synonyms: [], required: true, help: '' },
  status: {
    label: 'Estado',
    synonyms: [],
    help: 'Activo, Inactivo, Vendido o alquilado, Retirado.',
  },
  statusId: { label: 'StatusID', synonyms: [], help: '' },
  exclusive: { label: 'Exclusiva', synonyms: [], help: '' },
  listPrice: {
    label: 'Precio Lista',
    synonyms: [],
    help: 'De venta o de alquiler, según la operación.',
  },
  currency: { label: 'Moneda', synonyms: [], help: 'USD.' },
  department: { label: 'Depto', synonyms: [], help: '' },
  district: {
    label: 'Ciudad',
    synonyms: [],
    help: 'Distrito (o cantón, que se elige en la vista previa).',
  },
  listingStartsOn: { label: 'Listado', synonyms: [], help: '' },
  listingEndsOn: { label: 'Vencimiento', synonyms: [], help: '' },
  closedPrice: { label: 'Precio Venta', synonyms: [], help: 'Precio de cierre.' },
  closedOn: { label: 'Fecha Cierre', synonyms: [], help: '' },
  bedrooms: { label: 'Beds', synonyms: [], help: '' },
  bathrooms: { label: 'Baths', synonyms: [], help: '' },
  lotArea: { label: 'Terreno', synonyms: [], help: '' },
  lotAreaUnit: { label: 'Unid. Terreno', synonyms: [], help: 'Metro cuadrado o Vara cuadrada.' },
  builtArea: { label: 'Construcción', synonyms: [], help: 'En m².' },
  yearBuilt: { label: 'Año', synonyms: [], help: '' },
  parkingSpaces: { label: 'Parqueos', synonyms: [], help: '' },
  address: { label: 'Dirección', synonyms: [], help: 'Privada; se guarda cifrada.' },
  latitude: { label: 'Latitude', synonyms: [], help: '' },
  longitude: { label: 'Longitude', synonyms: [], help: '' },
  photoUrl: { label: 'Foto Principal', synonyms: [], help: 'Se descarga y procesa sin metadatos.' },
  assignedTo: {
    label: 'Agente',
    synonyms: [],
    help: 'Por nombre; si no existe, el agente por defecto.',
  },
} as const satisfies Record<string, ImportField>;
export type ReconnectField = keyof typeof RECONNECT_PROPERTY_FIELDS;

export type ImportFormat = 'standard' | 'reconnect';

/** RECONNECT's export, by the columns only it has. */
export function importFormat(kind: ImportKind, headers: readonly string[]): ImportFormat {
  const titles = new Set(headers.map(normalizeTitle));
  return kind === 'properties' &&
    ['listing id', 'statusid', 'foto principal', 'oficina'].every((t) => titles.has(t))
    ? 'reconnect'
    : 'standard';
}

/** The fields a file is mapped to: the template's, or RECONNECT's for its export. */
export function importFields(
  kind: ImportKind,
  headers: readonly string[],
): Record<string, ImportField> {
  return importFormat(kind, headers) === 'reconnect'
    ? RECONNECT_PROPERTY_FIELDS
    : IMPORT_FIELDS[kind];
}

/** RECONNECT's types (ours are coarser: the original is kept on the property). */
export const RECONNECT_TYPES: Record<string, PropertyType> = {
  'casa villa': 'house',
  casa: 'house',
  villa: 'house',
  apartamento: 'apartment',
  'lote terreno': 'land',
  lote: 'land',
  terreno: 'land',
  oficina: 'office',
  negocio: 'commercial',
  local: 'commercial',
  'local comercial': 'commercial',
  edificio: 'commercial',
  bodega: 'warehouse',
};

/** RECONNECT's statuses (StatusID, or the start of "Estado"). */
export const RECONNECT_STATUSES = {
  byId: { 1: 'inactive', 2: 'active', 5: 'withdrawn', 8: 'closed' } as Record<number, string>,
  byText: { inactivo: 'inactive', activo: 'active', retirado: 'withdrawn', vendido: 'closed' },
} as const;

/**
 * Names that are not the catalog's but mean one of its districts (old names, the
 * town that gives the district its name). Cantons and hamlets are not here: the
 * agency chooses them once and they are remembered (district_aliases).
 */
export const DISTRICT_EQUIVALENCES: Record<string, string> = {
  'nueva san salvador': 'santa tecla',
  'la herradura': 'san luis la herradura',
};

export const IMPORT_FIELDS: Record<ImportKind, Record<string, ImportField>> = {
  contacts: CONTACT_IMPORT_FIELDS,
  properties: PROPERTY_IMPORT_FIELDS,
};

/** Titles compared without accents, case, punctuation or extra spaces. */
export function normalizeTitle(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9%]+/g, ' ')
    .trim();
}

/** { field: column } proposed from the column titles (each column used once). */
export function suggestMapping(
  kind: ImportKind,
  headers: readonly string[],
): Record<string, number> {
  const normalized = headers.map((h) => normalizeTitle(h));
  const used = new Set<number>();
  const mapping: Record<string, number> = {};
  const fields = Object.entries(importFields(kind, headers));
  // Exact titles first (so "Teléfono 2" is not taken by "Teléfono"), then synonyms.
  for (const pass of ['label', 'synonyms'] as const) {
    for (const [key, field] of fields) {
      if (key in mapping) continue;
      const names =
        pass === 'label' ? [normalizeTitle(field.label)] : field.synonyms.map(normalizeTitle);
      const index = normalized.findIndex((h, i) => !used.has(i) && names.includes(h));
      if (index >= 0) {
        mapping[key] = index;
        used.add(index);
      }
    }
  }
  return mapping;
}

export const importMappingSchema = z
  .record(
    z.string().max(40),
    z
      .number()
      .int()
      .min(0)
      .max(IMPORT_LIMITS.maxColumns - 1),
  )
  .refine((m) => new Set(Object.values(m)).size === Object.values(m).length, {
    message: 'Una columna está asignada a dos campos',
  });

export const importOptionsSchema = z.strictObject({
  /**
   * Every imported contact (owners too) gets a consent with this basis: required,
   * except for RECONNECT's export, which creates no contacts (checked by the API).
   */
  legalBasis: z
    .enum(Object.keys(IMPORT_LEGAL_BASES) as [ImportLegalBasis], {
      message: 'Elige la base legal de los datos que importas',
    })
    .optional(),
  /** Rows without an agent go to this person (default: whoever imports). */
  defaultAssigneeId: z.uuid().optional(),
  /** Contacts only: also a deal in the first stage of the default pipeline. */
  createDeals: z.boolean().default(false),
  dealOperation: z
    .enum(Object.keys(DEAL_OPERATIONS) as [keyof typeof DEAL_OPERATIONS])
    .default('purchase'),
  channelId: z.uuid().optional(),
});
export type ImportOptions = z.output<typeof importOptionsSchema>;
export type ImportOptionsInput = z.input<typeof importOptionsSchema>;

export const importAnalyzeSchema = z.strictObject({
  mapping: importMappingSchema,
  options: importOptionsSchema,
});
export type ImportAnalyzeInput = z.input<typeof importAnalyzeSchema>;

/** The district chosen for a name of the file (remembered for the agency's imports). */
export const importDistrictChoicesSchema = z.strictObject({
  choices: z
    .array(
      z.strictObject({
        name: z.string().trim().min(1).max(120),
        department: z.string().trim().max(60).nullable(),
        districtId: z.number().int().positive(),
      }),
    )
    .min(1)
    .max(200),
});
export type ImportDistrictChoices = z.output<typeof importDistrictChoicesSchema>;

export const IMPORT_ROW_STATUSES = {
  pending: 'Sin revisar',
  valid: 'Lista para importar',
  duplicate: 'Duplicada',
  error: 'Con errores',
  created: 'Creada',
  failed: 'No se pudo crear',
  undone: 'Deshecha',
  kept: 'No se deshizo',
} as const;
export type ImportRowStatus = keyof typeof IMPORT_ROW_STATUSES;

export const importRowsQuerySchema = z.strictObject({
  status: z.enum(Object.keys(IMPORT_ROW_STATUSES) as [ImportRowStatus]).optional(),
  page: z.coerce.number().int().min(1).max(1000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});

// ---------------------------------------------------------------------------
// Cell values
// ---------------------------------------------------------------------------

/** A cell as read from the file: text, number, boolean, date (ISO) or empty. */
export type ImportCell = string | number | boolean | null;

/** Text of a cell ("" if empty); whole numbers without decimals or exponent. */
export function cellText(value: ImportCell | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toFixed(0) : String(value);
  }
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * "185,000.50", "$185000", "185.000,50" (European style, as some files come) → 185000.5.
 * undefined when it is not a number.
 */
export function parseNumber(value: ImportCell | undefined): number | null | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const text = cellText(value).replace(/\$|usd|\s/gi, '');
  if (text === '') return null;
  let normalized: string;
  if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) normalized = text.replace(/,/g, '');
  else if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(text))
    normalized = text.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d+(\.\d+)?$/.test(text)) normalized = text;
  else if (/^-?\d+,\d{1,2}$/.test(text)) normalized = text.replace(',', '.');
  else return undefined;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : undefined;
}

/** Sí/No, X, 1/0, true/false → boolean; "" → null; anything else → undefined. */
export function parseYesNo(value: ImportCell | undefined): boolean | null | undefined {
  if (typeof value === 'boolean') return value;
  const text = normalizeTitle(cellText(value));
  if (text === '') return null;
  if (['si', 's', 'x', '1', 'true', 'verdadero', 'yes'].includes(text)) return true;
  if (['no', 'n', '0', 'false', 'falso'].includes(text)) return false;
  return undefined;
}

/** dd/mm/aaaa, d-m-aaaa, aaaa-mm-dd or a date cell (ISO) → "aaaa-mm-dd". */
export function parseDate(value: ImportCell | undefined): string | null | undefined {
  const text = cellText(value);
  if (text === '') return null;
  let y: number;
  let m: number;
  let d: number;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  const local = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(text);
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (local) [d, m, y] = [Number(local[1]), Number(local[2]), Number(local[3])];
  else return undefined;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d)
    return undefined;
  return date.toISOString().slice(0, 10);
}

/** Matches a cell with a catalog by its labels (and a few extra words). */
export function matchChoice<K extends string>(
  value: ImportCell | undefined,
  choices: Record<K, string>,
  extra: Partial<Record<K, readonly string[]>> = {},
): K | null | undefined {
  const text = normalizeTitle(cellText(value));
  if (text === '') return null;
  for (const [key, label] of Object.entries(choices) as [K, string][]) {
    const names = [label, key, ...(extra[key] ?? [])].map(normalizeTitle);
    if (names.includes(text)) return key;
  }
  return undefined;
}

/** Split "Comprador, Propietario" / "a; b" into parts. */
export function splitList(value: ImportCell | undefined): string[] {
  return cellText(value)
    .split(/[,;|]/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Extra words accepted for catalog values. */
export const IMPORT_SYNONYMS = {
  contactKind: {
    person: ['persona natural', 'natural'],
    company: ['empresa', 'sociedad', 'juridica'],
  },
  contactType: {
    owner: ['propietaria', 'dueno', 'vendedor', 'arrendador'],
    buyer: ['compradora'],
    tenant: ['inquilina', 'arrendatario'],
    lead: ['prospecto', 'interesado'],
    external_broker: ['corredor', 'broker', 'agente externo'],
  },
  dealOperation: {
    purchase: ['compra', 'comprar', 'venta'],
    rent: ['alquiler', 'alquilar', 'renta'],
  },
  operation: {
    sale: ['vender', 'se vende'],
    rent: ['renta', 'arrendamiento', 'se alquila'],
    sale_rent: ['venta y renta', 'ambas', 'venta o alquiler'],
  },
  areaUnit: {
    m2: ['m2', 'mts', 'metros', 'metros cuadrados', 'metro cuadrado'],
    v2: ['v2', 'varas', 'varas cuadradas', 'vara cuadrada'],
  },
} as const;
